/**
 * shared.js — Centralized constants and utilities for news-intelligence module.
 *
 * Eliminates duplication of CATEGORY_COLORS, SENTIMENT configs, and time
 * formatting helpers that were previously copy-pasted across NewsCard,
 * FeaturedNewsHero, NewsTicker, and NewsMapPage.
 */

import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

/* ── Category colours ── */

export const CATEGORY_COLORS = {
  macro: '#3b82f6',
  policy: '#8b5cf6',
  industry: '#10b981',
  company: '#f59e0b',
  market: '#ef4444',
  central_bank: '#6366f1',
  earnings: '#14b8a6',
  geopolitical: '#f97316',
  investment_bank: '#ec4899',
};

export const CATEGORY_T_KEYS = {
  macro: 'catMacro',
  policy: 'catPolicy',
  industry: 'catIndustry',
  company: 'catCompany',
  market: 'catMarket',
  central_bank: 'catCentralBank',
  earnings: 'catEarnings',
  geopolitical: 'catGeopolitical',
  investment_bank: 'catInvestmentBank',
};

/* ── Sentiment configs ── */

export const SENTIMENT_CONFIG = {
  bullish: { color: '#10b981', IconComp: TrendingUp, tKey: 'bullish', label: '利好', labelEn: 'Bullish' },
  bearish: { color: '#ef4444', IconComp: TrendingDown, tKey: 'bearish', label: '利空', labelEn: 'Bearish' },
  neutral: { color: '#6b7280', IconComp: Minus, tKey: 'neutral', label: '中性', labelEn: 'Neutral' },
};

/** Sentiment dot colour map (for tickers / compact views) */
export const SENTIMENT_DOT = {
  bullish: '#10b981',
  bearish: '#ef4444',
  neutral: '#6b7280',
};

/* ── Category hero images (Unsplash free, multiple per category to avoid duplication in carousel) ── */

export const CATEGORY_IMAGE_POOL = {
  macro: [
    'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&h=400&fit=crop', // stock chart
    'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=800&h=400&fit=crop', // economy data
    'https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=800&h=400&fit=crop', // charts on screen
  ],
  policy: [
    'https://images.unsplash.com/photo-1523292562811-8fa7962a78c8?w=800&h=400&fit=crop', // government building
    'https://images.unsplash.com/photo-1575540325276-8ab598df7543?w=800&h=400&fit=crop', // courthouse
    'https://images.unsplash.com/photo-1541872703-74c5e44368f9?w=800&h=400&fit=crop', // capitol
  ],
  industry: [
    'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&h=400&fit=crop', // industry/tech
    'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=800&h=400&fit=crop', // robot
    'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&h=400&fit=crop', // circuit board
  ],
  company: [
    'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=400&fit=crop', // skyscraper
    'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=400&fit=crop', // office
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&h=400&fit=crop', // boardroom
  ],
  market: [
    'https://images.unsplash.com/photo-1642790106117-e829e14a795f?w=800&h=400&fit=crop', // trading floor
    'https://images.unsplash.com/photo-1535320903710-d993d3d77d29?w=800&h=400&fit=crop', // stock display
    'https://images.unsplash.com/photo-1559526324-593bc073d938?w=800&h=400&fit=crop', // bull statue
  ],
  central_bank: [
    'https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?w=800&h=400&fit=crop', // bank/money
    'https://images.unsplash.com/photo-1621761191319-c6fb62004040?w=800&h=400&fit=crop', // currency
    'https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=800&h=400&fit=crop', // vault
  ],
  earnings: [
    'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&h=400&fit=crop', // financial charts
    'https://images.unsplash.com/photo-1543286386-713bdd548da4?w=800&h=400&fit=crop', // calculator
    'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=400&fit=crop', // spreadsheet
  ],
  geopolitical: [
    'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&h=400&fit=crop', // globe
    'https://images.unsplash.com/photo-1521295121783-8a321d551ad2?w=800&h=400&fit=crop', // world map
    'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&h=400&fit=crop', // meeting
  ],
  investment_bank: [
    'https://images.unsplash.com/photo-1541354329998-f4d9a9f9297f?w=800&h=400&fit=crop', // wall street
    'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&h=400&fit=crop', // business meeting
    'https://images.unsplash.com/photo-1444653614773-995cb1ef9efa?w=800&h=400&fit=crop', // financial district
  ],
};

/** Pick a unique image for each featured article (no duplicates in carousel) */
export function pickHeroImage(category, index) {
  const pool = CATEGORY_IMAGE_POOL[category] || CATEGORY_IMAGE_POOL.market;
  return pool[index % pool.length];
}

/* ── Time formatting ── */

/**
 * Language-aware relative time string.
 * @param {string} dateStr  ISO date string
 * @param {string} lang     'zh' | 'en'
 */
export function formatTimeAgo(dateStr, lang) {
  if (!dateStr) return '';
  const now = Date.now();
  const date = new Date(dateStr);
  const diffMs = now - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (lang === 'zh') {
    if (diffMin < 1) return '刚刚';
    if (diffMin < 60) return `${diffMin}分钟前`;
    if (diffHr < 24) return `${diffHr}小时前`;
    if (diffDay < 7) return `${diffDay}天前`;
    return date.toLocaleDateString('zh-CN');
  }
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString('en-US');
}

/**
 * Short relative time (no language, compact).  Used in tickers.
 * Returns e.g. "5m", "3h", "1d".
 */
export function relTime(dt) {
  if (!dt) return '';
  const diff = (Date.now() - new Date(dt).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

function toText(value) {
  if (typeof value === 'string') return value;
  if (value == null) return '';
  return String(value);
}

function toNumber(value, fallback = 0) {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function clamp01(value) {
  return Math.max(0, Math.min(1, toNumber(value, 0)));
}

function normalizeSentiment(value) {
  if (value === 'bullish' || value === 'bearish' || value === 'neutral') {
    return value;
  }
  const numeric = toNumber(value, 0);
  if (numeric > 0.05) return 'bullish';
  if (numeric < -0.05) return 'bearish';
  return 'neutral';
}

function buildPreview(raw) {
  const text = toText(raw).replace(/\s+/g, ' ').trim();
  if (!text) return '';
  return text.length > 160 ? `${text.slice(0, 157)}...` : text;
}

export function normalizeNewsArticle(raw = {}) {
  const impactScore = clamp01(
    raw.impact_score ?? raw.relevance_score ?? raw.magnitude ?? raw.score
  );
  const contentPreview = buildPreview(
    raw.content_preview ?? raw.summary ?? raw.content_zh ?? raw.content
  );

  return {
    article_id: toText(raw.article_id || raw.id || raw.uuid),
    title: toText(raw.title),
    title_zh: toText(raw.title_zh),
    content_preview: contentPreview,
    source_name: toText(raw.source_name || raw.source),
    published_at: toText(raw.published_at || raw.publish_time || raw.created_at),
    category: toText(raw.category || 'other'),
    sentiment: normalizeSentiment(raw.sentiment ?? raw.direction ?? raw.sentiment_score),
    impact_score: impactScore,
    relevance_score: clamp01(raw.relevance_score ?? impactScore),
    url: toText(raw.url),
    language: toText(raw.language || 'zh'),
    latitude: toNumber(raw.latitude, 0),
    longitude: toNumber(raw.longitude, 0),
    region: toText(raw.region),
  };
}

export function normalizeRegionStats(raw) {
  if (Array.isArray(raw)) {
    return raw.reduce((acc, item) => {
      const key = toText(item?.region || item?.key);
      if (key) acc[key] = toNumber(item?.count, 0);
      return acc;
    }, {});
  }

  if (raw && typeof raw === 'object') {
    return Object.entries(raw).reduce((acc, [key, value]) => {
      acc[key] = toNumber(value, 0);
      return acc;
    }, {});
  }

  return {};
}

export function normalizeArticleDetailResponse(raw = {}) {
  const article = normalizeNewsArticle(raw.article || raw);
  const detailArticle = {
    ...article,
    content: toText(raw.article?.content ?? raw.content),
    content_zh: toText(raw.article?.content_zh ?? raw.content_zh),
    entities: Array.isArray(raw.article?.entities) ? raw.article.entities : [],
  };

  const impacts = Array.isArray(raw.impacts)
    ? raw.impacts.map((impact) => ({
      ts_code: toText(impact?.ts_code),
      direction: toNumber(impact?.direction, 0),
      magnitude: clamp01(impact?.magnitude),
      industry: toText(impact?.industry),
      reasoning: toText(impact?.reasoning),
    }))
    : [];

  return {
    success: raw.success !== false,
    article: detailArticle,
    credibility: raw.credibility && typeof raw.credibility === 'object' ? raw.credibility : null,
    impacts,
  };
}
